<template>
  <Dialog
    ref="dialog"
    :dialogTitle="dialogTitle"
    :dialogVisible="dialogVisible"
    :dialogModel="dialogModel"
    :rules="rules"
    dialogWidth="600px"
    @submitForm="handleSubmit"
    @closeDialog="closeDialog"
  ></Dialog>
</template>

<script>
import Dialog from '@/components/common/Dialog';
import {
  confirmQwAiQualityApi,
  remindOnlyQwAiQualityApi,
  falseHitQwAiQualityApi,
  noActionQwAiQualityApi,
  appealQwAiQualityApi,
  appealPassQwAiQualityApi,
  appealRejectQwAiQualityApi,
  batchConfirmQwAiQualityApi,
  batchRemindOnlyQwAiQualityApi,
  batchFalseHitQwAiQualityApi,
  batchNoActionQwAiQualityApi,
  batchAppealPassQwAiQualityApi,
  batchAppealRejectQwAiQualityApi,
} from '@/api/qwAiQuality';

const TYPE_CONFIG = {
  confirm: {
    title: '确认质检结果',
    api: confirmQwAiQualityApi,
    batch: false,
    fields: [{ prop: 'remark', label: '质检留言', required: false, placeholder: '请输入质检留言，最多100字' }],
  },
  remind_only: {
    title: '标记仅提醒',
    api: remindOnlyQwAiQualityApi,
    batch: false,
    fields: [{ prop: 'remark', label: '仅提醒说明', required: false, placeholder: '请输入说明，最多100字' }],
  },
  false_hit: {
    title: '标记误报',
    api: falseHitQwAiQualityApi,
    batch: false,
    fields: [{ prop: 'reason', label: '误报原因', required: true, placeholder: '请输入误报原因，最多100字' }],
  },
  no_action: {
    title: '标记无需处理',
    api: noActionQwAiQualityApi,
    batch: false,
    fields: [{ prop: 'remark', label: '处理说明', required: false, placeholder: '可选，最多100字' }],
  },
  appeal: {
    title: '提交申诉',
    api: appealQwAiQualityApi,
    batch: false,
    fields: [
      { prop: 'reason', label: '申诉原因', required: true, placeholder: '请说明申诉原因，最多100字' },
      { prop: 'attachments', label: '图片附件', required: false, isImageList: true },
    ],
  },
  appeal_pass: {
    title: '申诉通过',
    api: appealPassQwAiQualityApi,
    batch: false,
    fields: [{ prop: 'remark', label: '处理说明', required: false, placeholder: '请输入处理说明，最多100字' }],
  },
  appeal_reject: {
    title: '申诉驳回',
    api: appealRejectQwAiQualityApi,
    batch: false,
    fields: [{ prop: 'remark', label: '处理说明', required: true, placeholder: '请输入处理说明，最多100字' }],
  },
  batch_confirm: {
    title: '批量确认',
    api: batchConfirmQwAiQualityApi,
    batch: true,
    fields: [{ prop: 'remark', label: '批量处理说明', required: false, placeholder: '可选，最多100字' }],
  },
  batch_remind_only: {
    title: '批量仅提醒',
    api: batchRemindOnlyQwAiQualityApi,
    batch: true,
    fields: [{ prop: 'remark', label: '批量处理说明', required: false, placeholder: '可选，最多100字' }],
  },
  batch_false_hit: {
    title: '批量误报',
    api: batchFalseHitQwAiQualityApi,
    batch: true,
    fields: [{ prop: 'reason', label: '误报原因', required: true, placeholder: '请输入误报原因，最多100字' }],
  },
  batch_no_action: {
    title: '批量无需处理',
    api: batchNoActionQwAiQualityApi,
    batch: true,
    fields: [{ prop: 'remark', label: '批量处理说明', required: false, placeholder: '可选，最多100字' }],
  },
  batch_appeal_pass: {
    title: '批量审核通过',
    api: batchAppealPassQwAiQualityApi,
    batch: true,
    fields: [{ prop: 'remark', label: '批量处理说明', required: false, placeholder: '可选，最多100字' }],
  },
  batch_appeal_reject: {
    title: '批量审核驳回',
    api: batchAppealRejectQwAiQualityApi,
    batch: true,
    fields: [{ prop: 'remark', label: '批量驳回说明', required: true, placeholder: '请输入批量驳回说明，最多100字' }],
  },
};

export default {
  components: { Dialog },
  data() {
    return {
      dialogVisible: false,
      currentType: '',
      targetIds: [],
    };
  },
  computed: {
    config() {
      return TYPE_CONFIG[this.currentType] || { title: '', fields: [] };
    },
    dialogTitle() {
      return this.config.title;
    },
    dialogModel() {
      return this.config.fields.map(field => {
        if (field.isImageList) {
          return {
            type: 'el-upload-image-list',
            label: field.label,
            prop: field.prop,
            tips: '支持多图',
          };
        }
        return {
          type: 'el-input-textarea',
          label: field.label,
          prop: field.prop,
          placeholder: field.placeholder,
          maxlength: 100,
          showWordLimit: true,
        };
      });
    },
    rules() {
      const rules = {};
      this.config.fields.forEach(field => {
        if (field.required) {
          rules[field.prop] = [{ required: true, message: `${field.label}不能为空`, trigger: 'blur' }];
        }
      });
      return rules;
    },
  },
  methods: {
    // type: TYPE_CONFIG 中配置的单条或批量操作编码
    // ids: 单条操作传单个 id；批量操作传 id 数组
    open(type, ids) {
      this.currentType = type;
      this.targetIds = Array.isArray(ids) ? ids : [ids];
      this.dialogVisible = true;
    },
    async handleSubmit(form) {
      const config = this.config;
      if (!config.api) {
        return;
      }
      const params = config.batch ? Object.assign({}, form, { ids: this.targetIds }) : Object.assign({}, form, { id: this.targetIds[0] });
      const data = await config.api(params);
      if (data.code !== 200) {
        this.$message.error(data.message);
        return;
      }
      const result = data.data || {};
      if (config.batch && result.failed && result.failed.length) {
        this.showBatchFailures(result);
      } else {
        this.$message.success(data.message || '操作成功');
      }
      this.closeDialog();
      this.$emit('success', result);
    },
    showBatchFailures(result) {
      const successCount = (result.success_ids || []).length;
      const failed = result.failed || [];
      const failureItems = failed.map(item =>
        this.$createElement(
          'div',
          {
            style: {
              marginBottom: '4px',
              wordBreak: 'break-all',
            },
          },
          `ID ${item.id}：${item.reason || '未知原因'}`,
        ),
      );
      this.$notify({
        title: `批量操作完成：成功 ${successCount} 条，失败 ${failed.length} 条`,
        message: this.$createElement(
          'div',
          {
            style: {
              maxHeight: '240px',
              overflowY: 'auto',
            },
          },
          failureItems,
        ),
        type: successCount ? 'warning' : 'error',
        duration: 0,
      });
    },
    closeDialog() {
      this.dialogVisible = false;
      this.$refs.dialog.resetForm();
    },
  },
};
</script>
